import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

import CityAutoCompleteSelect from 'components/InputsField/formikInputs/CityAutoCompleteSelect';
import CountrySelect from 'components/InputsField/formikInputs/CountrySelect';
import FormikTextField from 'components/InputsField/formikInputs/FormikTextField';
import FormRow, { ALIGN } from 'components/common/Layout/FormRow';
import AdditionalAddressRow from './AdditionalAddressRow';

import { radiusInput } from 'components/InputsField/inputFormats';
import styles from './styles.module.scss';

const MAX_ADDITIONAL_ADDRESSES = 6;

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M10.2462 9.00407L7.35193 6.11131L10.2462 3.21856C10.4108 3.05405 10.5032 2.83094 10.5032 2.59829C10.5032 2.36565 10.4108 2.14253 10.2462 1.97803C10.0816 1.81352 9.85835 1.72111 9.62559 1.72111C9.39283 1.72111 9.1696 1.81352 9.00501 1.97803L6.11077 4.87079L3.21687 1.97837C3.1382 1.89205 3.04237 1.82309 2.93551 1.7759C2.82864 1.72872 2.71311 1.70435 2.59628 1.70435C2.47946 1.70435 2.36392 1.72872 2.25706 1.7759C2.1502 1.82309 2.05437 1.89205 1.9757 1.97837C1.89061 2.05776 1.82277 2.15379 1.77638 2.26049C1.72999 2.36719 1.70605 2.48229 1.70605 2.59863C1.70605 2.71497 1.72999 2.83007 1.77638 2.93678C1.82277 3.04348 1.89061 3.13951 1.9757 3.2189L4.86994 6.11165L1.9757 9.00441C1.89061 9.0838 1.82277 9.17983 1.77638 9.28653C1.72999 9.39323 1.70605 9.50833 1.70605 9.62467C1.70605 9.74101 1.72999 9.85611 1.77638 9.96282C1.82277 10.0695 1.89061 10.1656 1.9757 10.2449C2.05679 10.3271 2.15341 10.3923 2.25995 10.4369C2.36648 10.4814 2.48081 10.5043 2.59628 10.5043C2.71176 10.5043 2.82608 10.4814 2.93262 10.4369C3.03915 10.3923 3.13577 10.3271 3.21687 10.2449L6.11111 7.35218L9.00535 10.2449C9.17061 10.4082 9.39359 10.4997 9.62593 10.4997C9.85827 10.4997 10.0812 10.4082 10.2465 10.2449C10.3288 10.1639 10.394 10.0672 10.4386 9.96073C10.4832 9.8542 10.5061 9.73989 10.5061 9.62443C10.506 9.50898 10.483 9.39468 10.4384 9.28818C10.3938 9.18168 10.3285 9.08511 10.2462 9.00407Z" fill="white"/>
  </svg>
);

function SearchForm({ isSimplified = false }) {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext();

  // Exclusions visibility (UI-only state)
  const [showLoadingExclusions, setShowLoadingExclusions] = useState(false);
  const [showUnloadingExclusions, setShowUnloadingExclusions] = useState(false);

  // Get values from Formik state
  const excludedLoadingCountries = values.excluded_loading_countries || [];
  const excludedUnloadingCountries = values.excluded_unloading_countries || [];
  const additionalLoadingAddresses = values.additional_loading_addresses || [];
  const additionalUnloadingAddresses = values.additional_unloading_addresses || [];

  // Auto-show exclusions section if loaded from template
  useEffect(() => {
    if (excludedLoadingCountries.length > 0) {
      setShowLoadingExclusions(true);
    }
  }, [excludedLoadingCountries.length]);

  useEffect(() => {
    if (excludedUnloadingCountries.length > 0) {
      setShowUnloadingExclusions(true);
    }
  }, [excludedUnloadingCountries.length]);

  const handleSwitch = () => {
    setFieldValue('loading_address', values.unloading_address);
    setFieldValue('unloading_address', values.loading_address);
    setFieldValue('loading_address_radius', values.unloading_address_radius);
    setFieldValue('unloading_address_radius', values.loading_address_radius);
    // Swap excluded countries
    setFieldValue('excluded_loading_countries', excludedUnloadingCountries);
    setFieldValue('excluded_unloading_countries', excludedLoadingCountries);
    // Swap additional addresses
    setFieldValue('additional_loading_addresses', additionalUnloadingAddresses);
    setFieldValue('additional_unloading_addresses', additionalLoadingAddresses);
  };

  // Toggle exclusions
  const handleToggleLoadingExclusions = useCallback(() => {
    setShowLoadingExclusions((prev) => !prev);
  }, []);

  const handleToggleUnloadingExclusions = useCallback(() => {
    setShowUnloadingExclusions((prev) => !prev);
  }, []);

  // Clear exclusions
  const handleClearLoadingExclusions = useCallback(() => {
    setFieldValue('excluded_loading_countries', []);
    setShowLoadingExclusions(false);
  }, [setFieldValue]);

  const handleClearUnloadingExclusions = useCallback(() => {
    setFieldValue('excluded_unloading_countries', []);
    setShowUnloadingExclusions(false);
  }, [setFieldValue]);

  // Additional addresses handlers
  const handleAddLoadingAddress = useCallback(() => {
    if (additionalLoadingAddresses.length < MAX_ADDITIONAL_ADDRESSES) {
      setFieldValue('additional_loading_addresses', [
        ...additionalLoadingAddresses,
        { id: Date.now(), address: null },
      ]);
    }
  }, [additionalLoadingAddresses, setFieldValue]);

  const handleAddUnloadingAddress = useCallback(() => {
    if (additionalUnloadingAddresses.length < MAX_ADDITIONAL_ADDRESSES) {
      setFieldValue('additional_unloading_addresses', [
        ...additionalUnloadingAddresses,
        { id: Date.now(), address: null },
      ]);
    }
  }, [additionalUnloadingAddresses, setFieldValue]);

  const handleRemoveLoadingAddress = useCallback(
    (id) => {
      setFieldValue(
        'additional_loading_addresses',
        additionalLoadingAddresses.filter((item) => item.id !== id)
      );
    },
    [additionalLoadingAddresses, setFieldValue]
  );

  const handleRemoveUnloadingAddress = useCallback(
    (id) => {
      setFieldValue(
        'additional_unloading_addresses',
        additionalUnloadingAddresses.filter((item) => item.id !== id)
      );
    },
    [additionalUnloadingAddresses, setFieldValue]
  );

  const handleLoadingAddressChange = useCallback(
    (id, address) => {
      setFieldValue(
        'additional_loading_addresses',
        additionalLoadingAddresses.map((item) => (item.id === id ? { ...item, address } : item))
      );
    },
    [additionalLoadingAddresses, setFieldValue]
  );

  const handleUnloadingAddressChange = useCallback(
    (id, address) => {
      setFieldValue(
        'additional_unloading_addresses',
        additionalUnloadingAddresses.map((item) => (item.id === id ? { ...item, address } : item))
      );
    },
    [additionalUnloadingAddresses, setFieldValue]
  );

  const handleAddressSelected = useCallback(
    (radiusFieldName) => (option) => {
      const isCity = Boolean(option?.value?.city);
      if (isCity) {
        if (!values[radiusFieldName]) {
          setFieldValue(radiusFieldName, '50');
        }
      } else {
        setFieldValue(radiusFieldName, '');
      }
    },
    [setFieldValue, values]
  );

  return (
    <FormRow withSpace align={ALIGN.top}>
      {/* ОТКУДА Section */}
      <div className={styles.direction}>
        <div className={styles.labelRow}>
          <span className={styles.fieldLabel}>{t('main.search.from')}</span>
          {!isSimplified && (
            <button
              type="button"
              className={styles.excludeToggle}
              onClick={handleToggleLoadingExclusions}
            >
              {t('cargoSearch.exclude', 'Исключить')}
            </button>
          )}
        </div>

        <div className={styles.inputRow}>
          <CityAutoCompleteSelect
            noOptionsMessage={t('cargoFields.itinerary_section.no_option')}
            name="loading_address"
            label=""
            className={styles.city}
            isClearable={true}
            useMapboxSearch
            withMapPicker
            onAddressChange={handleAddressSelected('loading_address_radius')}
          />
          <FormikTextField
            label=""
            name="loading_address_radius"
            maxLength="64"
            placeholder={t('main.search.radius')}
            postfix={t('main.search.distance')}
            wrapperClassName={styles.radius}
            format={radiusInput}
          />
        </div>
        {!isSimplified && additionalLoadingAddresses.length < MAX_ADDITIONAL_ADDRESSES && (
          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.addLocationBtn}
              onClick={handleAddLoadingAddress}
            >
              + {t('cargoSearch.addLocation', 'Добавить местоположение')}
            </button>
          </div>
        )}

        {/* Additional loading addresses */}
        {!isSimplified && additionalLoadingAddresses.map((item) => (
          <AdditionalAddressRow
            key={item.id}
            id={item.id}
            address={item.address}
            onAddressChange={handleLoadingAddressChange}
            onRemove={handleRemoveLoadingAddress}
          />
        ))}

        {/* Excluded loading countries */}
        {!isSimplified && showLoadingExclusions && (
          <div className={styles.exclusionsSection}>
            <span className={styles.exclusionLabel}>
              {t('cargoSearch.excludeLocations', 'Исключить местонахождение')}
            </span>
            <div className={styles.exclusionRow}>
              <CountrySelect
                name="excluded_loading_countries"
                placeholder={t('forms.startEnter', 'Начните вводить')}
                value={excludedLoadingCountries}
                onChange={(countries) => setFieldValue('excluded_loading_countries', countries)}
                isMulti={true}
                isClearable={true}
                noOptionsMessage={t('forms.country.no_option')}
                className={styles.exclusionSelect}
              />
              <button
                type="button"
                className={styles.clearBtn}
                onClick={handleClearLoadingExclusions}
                aria-label={t('common.close', 'Закрыть')}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        )}

        {/* Direction switch button */}
        <button className={styles.directionSwitch} onClick={handleSwitch} type="button">
          <img
            src={new URL('../../../img/search-switch.svg', import.meta.url).href}
            alt=""
          />
        </button>
      </div>

      {/* КУДА Section */}
      <div className={styles.direction}>
        <div className={styles.labelRow}>
          <span className={styles.fieldLabel}>{t('main.search.destination')}</span>
          {!isSimplified && (
            <button
              type="button"
              className={styles.excludeToggle}
              onClick={handleToggleUnloadingExclusions}
            >
              {t('cargoSearch.exclude', 'Исключить')}
            </button>
          )}
        </div>

        <div className={styles.inputRow}>
          <CityAutoCompleteSelect
            noOptionsMessage={t('cargoFields.itinerary_section.no_option')}
            name="unloading_address"
            label=""
            className={styles.city}
            isClearable={true}
            useMapboxSearch
            withMapPicker
            onAddressChange={handleAddressSelected('unloading_address_radius')}
          />
          <FormikTextField
            label=""
            name="unloading_address_radius"
            maxLength="64"
            placeholder={t('main.search.radius')}
            postfix={t('main.search.distance')}
            wrapperClassName={styles.radius}
            format={radiusInput}
          />
        </div>
        {!isSimplified && additionalUnloadingAddresses.length < MAX_ADDITIONAL_ADDRESSES && (
          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.addLocationBtn}
              onClick={handleAddUnloadingAddress}
            >
              + {t('cargoSearch.addLocation', 'Добавить местоположение')}
            </button>
          </div>
        )}

        {/* Additional unloading addresses */}
        {!isSimplified && additionalUnloadingAddresses.map((item) => (
          <AdditionalAddressRow
            key={item.id}
            id={item.id}
            address={item.address}
            onAddressChange={handleUnloadingAddressChange}
            onRemove={handleRemoveUnloadingAddress}
          />
        ))}

        {/* Excluded unloading countries */}
        {!isSimplified && showUnloadingExclusions && (
          <div className={styles.exclusionsSection}>
            <span className={styles.exclusionLabel}>
              {t('cargoSearch.excludeLocations', 'Исключить местонахождение')}
            </span>
            <div className={styles.exclusionRow}>
              <CountrySelect
                name="excluded_unloading_countries"
                placeholder={t('forms.startEnter', 'Начните вводить')}
                value={excludedUnloadingCountries}
                onChange={(countries) => setFieldValue('excluded_unloading_countries', countries)}
                isMulti={true}
                isClearable={true}
                noOptionsMessage={t('forms.country.no_option')}
                className={styles.exclusionSelect}
              />
              <button
                type="button"
                className={styles.clearBtn}
                onClick={handleClearUnloadingExclusions}
                aria-label={t('common.close', 'Закрыть')}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </FormRow>
  );
}

export default SearchForm;
